import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
} from 'react';

import { getCreatorQualitySignal } from '../api/creatorQualitySignals';
import type { CreatorSignalQualityResponse } from '../types/organizer';

export interface CreatorQualityContextValue {
    creatorSignalData: CreatorSignalQualityResponse | null;
    isLoading: boolean;
    error: Error | null;
}

const CreatorQualityContext = createContext<
    CreatorQualityContextValue | undefined
>(undefined);

export interface CreatorQualityProviderProps {
    organizerId: string;
    children: React.ReactNode;
}

export const CreatorQualityProvider: React.FC<CreatorQualityProviderProps> = ({
    organizerId,
    children,
}) => {
    const [creatorSignalData, setCreatorSignalData] = useState<
        CreatorSignalQualityResponse | null
    >(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const fetchQuality = useCallback((id: string, signal: AbortSignal) => {
        setCreatorSignalData(null);
        setIsLoading(true);
        setError(null);

        getCreatorQualitySignal(id, signal)
            .then((result) => {
                if (!signal.aborted) {
                    setCreatorSignalData(result);
                    setError(null);
                }
            })
            .catch((e) => {
                if (e.name === 'AbortError' || signal.aborted) {
                    return;
                }
                setError(e instanceof Error ? e : new Error(String(e)));
                setCreatorSignalData(null);
            })
            .finally(() => {
                if (!signal.aborted) {
                    setIsLoading(false);
                }
            });
    }, []);

    useEffect(() => {
        if (!organizerId) {
            setIsLoading(false);
            return () => {};
        }

        const abortController = new AbortController();
        fetchQuality(organizerId, abortController.signal);

        return () => {
            abortController.abort();
        };
    }, [organizerId, fetchQuality]);

    const value: CreatorQualityContextValue = {
        creatorSignalData,
        isLoading,
        error,
    };

    return (
        <CreatorQualityContext.Provider value={value}>
            {children}
        </CreatorQualityContext.Provider>
    );
};

export const useOrganizerQualityInfo = (): CreatorQualityContextValue => {
    const context = useContext(CreatorQualityContext);

    if (context === undefined) {
        throw new Error(
            'useOrganizerQualityInfo must be used within a CreatorQualityProvider',
        );
    }

    return context;
};
